import React from 'react';
import {
    PencilSquareIcon,
    ArrowUpTrayIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

// Helper Components (Consider moving to a shared location if not already)
const LoadingSpinner = ({ size = 'md' }) => <span className={`loading loading-spinner loading-${size} text-primary`}></span>;
const ErrorMessage = ({ message }) => (
    <div role="alert" className="alert alert-error my-2 py-2 text-xs shadow-sm">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>{message || 'An unexpected error occurred.'}</span>
    </div>
);
const SuccessMessage = ({ message }) => (
    <div role="alert" className="alert alert-success my-2 py-2 text-xs shadow-sm">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>{message}</span>
    </div>
);

const UserProfileCard = ({
    profile,
    isLoadingProfile,
    isEditingProfile,
    onToggleEdit,
    avatarPreview,
    onFileSelect,
    editFirstName,
    onFirstNameChange,
    editLastName,
    onLastNameChange,
    selectedFile,
    onSubmit,
    isUpdatingProfile,
    profileUpdateError,
    profileUpdateSuccess,
    displayInitial,
    displayName,
    isMobile = false, // Add isMobile prop for slight styling differences
}) => {

    const avatarSizeClasses = isMobile ? "w-24 h-24" : "w-20 h-20 md:w-24 md:h-24";
    const avatarInitialTextSize = isMobile ? "text-2xl" : "text-2xl md:text-3xl";

    return (
        <div className={`card bg-base-100 shadow-md p-4 ${isMobile ? 'h-60 overflow-y-auto' : ''}`}>
            <div className="flex justify-between items-start mb-3">
                <h2 className="card-title text-lg">{isMobile ? "My Profile" : "My Profile"}</h2>
                {!isEditingProfile && (
                    <button onClick={onToggleEdit} className="btn btn-ghost btn-xs p-1" title="Edit Profile">
                        <PencilSquareIcon className="w-5 h-5 text-primary"/>
                    </button>
                )}
            </div>
            {profileUpdateError && <ErrorMessage message={profileUpdateError} />}
            {profileUpdateSuccess && !isEditingProfile && <SuccessMessage message={profileUpdateSuccess} />}
            {isEditingProfile ? (
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="form-control items-center">
                        <div className="avatar mb-2">
                            <div className={`${avatarSizeClasses} rounded-full ring ring-primary ring-offset-base-100 ring-offset-2`}>
                                <img
                                    src={avatarPreview || `https://ui-avatars.com/api/?name=${encodeURIComponent(editFirstName || editLastName || displayInitial || 'P')}&background=random&size=128`}
                                    alt="Avatar Preview"
                                    className="rounded-full w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(editFirstName || editLastName || displayInitial || 'P')}&background=random&size=128`;
                                    }}
                                />
                            </div>
                        </div>
                        <label htmlFor={`avatarUpload-${isMobile ? 'mobile' : 'desktop'}`} className="btn btn-outline btn-xs gap-2 mt-2 mb-4">
                            <ArrowUpTrayIcon className="w-4 h-4" />
                            {selectedFile ? 'Change Image' : 'Upload Image'}
                        </label>
                        <input
                            type="file"
                            id={`avatarUpload-${isMobile ? 'mobile' : 'desktop'}`}
                            accept="image/png, image/jpeg, image/gif"
                            onChange={onFileSelect}
                            className="hidden"
                            disabled={isUpdatingProfile}
                        />
                        {selectedFile && <p className="text-xs text-base-content/70 mt-1 max-w-full truncate" title={selectedFile.name}>Selected: {selectedFile.name}</p>}
                    </div>
                    <div className="form-control">
                        <label className="label py-0"><span className="label-text text-xs">First Name</span></label>
                        <input type="text" placeholder="Your first name" className="input input-bordered input-sm w-full" value={editFirstName} onChange={(e) => onFirstNameChange(e.target.value)} disabled={isUpdatingProfile} />
                    </div>
                    <div className="form-control">
                        <label className="label py-0"><span className="label-text text-xs">Last Name</span></label>
                        <input type="text" placeholder="Your last name" className="input input-bordered input-sm w-full" value={editLastName} onChange={(e) => onLastNameChange(e.target.value)} disabled={isUpdatingProfile} />
                    </div>
                    <div className="card-actions justify-end gap-2 mt-2">
                        <button type="button" onClick={onToggleEdit} className="btn btn-ghost btn-sm" disabled={isUpdatingProfile}>Cancel</button>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={isUpdatingProfile}>
                            {isUpdatingProfile ? <LoadingSpinner size="xs" /> : <><CheckCircleIcon className="w-4 h-4 mr-1"/> Save</>}
                        </button>
                    </div>
                </form>
            ) : (
                <div className="text-center">
                    <div className="avatar mb-4 justify-center">
                        {isLoadingProfile && !profile ? (
                            <div className={`${avatarSizeClasses} rounded-full flex items-center justify-center bg-base-300`}><LoadingSpinner size="sm" /></div>
                        ) : profile && profile.avatar_url ? (
                            <div className={`${avatarSizeClasses} rounded-full ring ring-primary ring-offset-base-100 ring-offset-2 relative overflow-hidden`}>
                                <img src={profile.avatar_url} alt={`${displayName} avatar`} className="rounded-full w-full h-full object-cover"
                                     onError={(e) => {
                                         e.target.style.display='none'; 
                                         const placeholder = e.target.nextElementSibling;
                                         if(placeholder && placeholder.classList.contains('avatar-initial-placeholder')) placeholder.style.display='flex';
                                     }}/>
                                <div className={`avatar-initial-placeholder bg-neutral text-neutral-content rounded-full w-full h-full flex items-center justify-center absolute top-0 left-0 ${avatarInitialTextSize}`}
                                     style={{display: (profile && profile.avatar_url) ? 'none' : 'flex'}}>
                                    <span>{displayInitial}</span>
                                </div>
                            </div>
                        ) : ( 
                            <div className={`bg-neutral text-neutral-content rounded-full ${avatarSizeClasses} ring ring-primary ring-offset-base-100 ring-offset-2 flex items-center justify-center`}>
                                <span className={`${avatarInitialTextSize}`}>{displayInitial}</span>
                            </div>
                        )}
                    </div>
                    <h2 className={`text-lg font-semibold truncate ${isMobile ? '' : 'md:text-xl'}`} title={displayName}>{displayName}</h2>
                </div>
            )}
        </div>
    );
}

export default UserProfileCard; 